/* eslint-disable @typescript-eslint/no-use-before-define */
import { isUndefined } from 'lodash';
import qs from 'qs';

export function getTopLevelDomain(hostname: string = window.location.hostname) {
    const parts = hostname.split('.').reverse();
    if (parts[0] === 'localhost') {
        return 'zawa.ai';
    } else {
        return parts[1] + '.' + parts[0];
    }
}

/**
 * 获取地址参数对象
 * @return
 */
export const getQuery = () => {
    if (typeof window !== 'undefined') {
        return parseQueryString(window.location.search) as {
            [key: string]: any;
        };
    }
    return {};
};

export function stringifyQuery(params: { [key: string]: any }, sort = false) {
    return qs.stringify(params, sort ? { sort: (a, b) => a.localeCompare(b) } : undefined);
}

export function parseQueryString(query: string) {
    // Remove any leading '?' from the query string
    if (query.startsWith('?')) {
        query = query.slice(1);
    }
    return qs.parse(query);
}

/**
 * 获取指定的参数
 * @param key
 * @param defaultValue
 * @param query
 * @return
 */
export const getQueryParam = (key: string, defaultValue?: any, query?: any) => {
    if (typeof window !== 'undefined') {
        const q = query || getQuery();
        return q[key] || defaultValue;
    }
    return '';
};

const filterEmpty = (trackObj: { [key: string]: any }) => {
    Object.keys(trackObj).forEach((key) => {
        const val = trackObj[key];
        if (isUndefined(val)) {
            delete trackObj[key];
        }
    });

    return trackObj;
};

/**
 * 替换url地址
 * @param params
 */
function replaceLocation(params: { [key: string]: any }) {
    const query = getQuery();
    const finalQuery = filterEmpty({ ...query, ...params });
    // 透传 window.history.state，避免覆盖 Next.js router 的 __N 元数据导致后退失效
    window.history.replaceState(window.history.state, '', getNewUrl(finalQuery));
}

/**
 * 向地址栏追加参数
 * @param params
 * @param replace 参数存在，是否替换
 */
export const appendQueryParams = (params: { [key: string]: any }, replace = true) => {
    const query = getQuery();
    Object.keys(params).forEach((key) => {
        const val = params[key];
        if (replace) {
            query[key] = val;
        } else {
            if (!query[key]) {
                query[key] = val;
            }
        }
    });
    replaceLocation(query);
};

/**
 * 移除URL链接上的参数
 * @param params {string[] | string} 参数名称
 */
export const deleteQueryParams = (params: string[] | string) => {
    const query = getQuery();
    if (Array.isArray(params)) {
        params.forEach((key: any) => {
            delete query[key];
        });
    } else {
        delete query[params];
    }
    // 透传 window.history.state，避免覆盖 Next.js router 的 __N 元数据导致后退失效
    window.history.replaceState(window.history.state, '', getNewUrl(query));
};

/**
 * 获取当前path下，指定参数的新地址
 * @param params
 * @returns
 */
export function getNewUrl(params: { [key: string]: any }) {
    const queryStr = stringifyQuery(params);
    return `${window.location.origin}${window.location.pathname}${queryStr ? '?' + queryStr : ''}`;
}

/**
 * 根据 URL 获取文件后缀名（扩展名）
 * @description xdesign-widgets 内暂无可复用方法：utils 的 `getSuffix` 是取环境后缀，
 * upload/sdk.ts 中的 `suffix` 取自 File 的 MIME 类型，均不适用于「从 URL 解析后缀」的场景，故新增此方法。
 * @param url 文件地址，支持携带 query / hash 的地址
 * @returns 小写后缀（不含点），无法识别时返回空字符串。例如 `https://x.com/a/b.PNG?v=1#t` => `png`
 */
export function getFileExtensionByUrl(url: string): string {
    if (!url || typeof url !== 'string') return '';

    // 兼容 base64 Data URL，例如 `data:image/png;base64,...` => `png`
    if (url.startsWith('data:')) {
        const mimeMatch = url.match(/^data:([^;,]+)/);
        if (mimeMatch) {
            const mime = mimeMatch[1]; // e.g. "image/png", "image/svg+xml"
            const subType = mime.split('/')[1] || '';
            // 处理复合子类型，如 svg+xml => svg, jpeg => jpeg
            const ext = subType.split('+')[0];
            return ext.toLowerCase();
        }
        return '';
    }

    // 去掉 query 与 hash，仅保留路径，再取最后一段文件名
    const fileName = url.split(/[?#]/)[0].split('/').pop() || '';
    const dotIndex = fileName.lastIndexOf('.');
    // dotIndex <= 0 表示无后缀，或点位于开头（如 `.env` 这类无扩展名场景）
    if (dotIndex <= 0) return '';
    return fileName.slice(dotIndex + 1).toLowerCase();
}

/**
 * 解析 Data URL（base64 或 URL 编码）的字节大小，无需网络请求
 * @example data:image/png;base64,iVBOR... => 按 base64 长度推算
 * @example data:text/plain,Hello%20World => 按解码后字节数计算
 */
function getDataUrlSize(url: string): number {
    const commaIndex = url.indexOf(',');
    if (commaIndex === -1) return 0;

    const meta = url.slice(5, commaIndex);
    const data = url.slice(commaIndex + 1);

    if (meta.includes(';base64')) {
        const padding = data.endsWith('==') ? 2 : data.endsWith('=') ? 1 : 0;
        return Math.floor((data.length * 3) / 4) - padding;
    }

    try {
        return new TextEncoder().encode(decodeURIComponent(data)).length;
    } catch {
        return new TextEncoder().encode(data).length;
    }
}

/**
 * 根据 URL 获取文件大小（单位：字节）
 * @description 按 URL 类型选择策略，均不发起完整文件下载：
 * - `data:` — 本地解析 base64 / URL 编码内容
 * - 其余（含 `blob:`、HTTP(S)）— HEAD 请求读取 `Content-Length`
 * 获取失败时返回 0。
 * @param url 文件地址
 * @returns 文件字节数；获取失败时返回 0
 */
export async function getFileSizeByUrl(url: string): Promise<number> {
    if (!url || typeof url !== 'string') return 0;

    if (url.startsWith('data:')) {
        return getDataUrlSize(url);
    }

    try {
        const headRes = await fetch(url, { method: 'HEAD' });
        const len = headRes.headers.get('content-length');
        if (headRes.ok && len) {
            return Number(len) || 0;
        }
    } catch {
        // HEAD 可能因跨域 / 服务端不支持而失败
    }
    return 0;
}
